import React, { ReactNode } from 'react';
import classnames from '../../../utils/classnames.tsx';
import NavigationItemBadge from './NavigationItemBadge.tsx';
import { useNavigationContext } from './NavigationContext.tsx';
import { faBolt, faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../Icon.tsx';
import Tooltip from '../../Tooltip.tsx';
import { useTranslation } from 'react-i18next';

export interface NavigationButtonProps {
    icon?: ReactNode;
    ellipsis?: boolean; // maybe we could use children or iconAfter instead of ellipsis
    label: string;
    active?: boolean;
    disabled?: boolean;
    badgeNumber?: number;
    newBadge?: ReactNode;
    automationEnabled?: boolean;
    className?: string;
    onClick?: (event: React.MouseEvent) => void;
}

const EllipsisIcon = withIcon(faEllipsisVertical);
const AutomationEnabledIcon = withIcon(faBolt);

const NavigationButton = ({
    icon,
    ellipsis = false,
    label,
    active = false,
    badgeNumber = undefined,
    newBadge = undefined,
    className,
    onClick,
    automationEnabled = false,
    ...buttonProps
}: NavigationButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const { t } = useTranslation('core');
    const { collapsed } = useNavigationContext();

    return (
        <button
            className={classnames(
                'relative flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-content-strong h-9 transition-colors font-medium',
                'cursor-pointer hover:bg-surface-strong transition-[padding] ease-in-out duration-200',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                collapsed && 'justify-start ',
                'whitespace-nowrap',
                className
            )}
            data-active={active}
            onClick={(event) => {
                onClick(event);
            }}
            {...buttonProps}
        >
            {icon ? <span className="w-4 inline-flex justify-center flex-none">{icon}</span> : null}

            {!collapsed ? (
                <>
                    <Tooltip content={label} className="inline-block w-auto min-w-0" side="top" overflowOnly>
                        <span className="min-w-0 truncate">{label}</span>
                    </Tooltip>

                    {automationEnabled && (
                        <Tooltip content={t('components.navigation.automationEnabled')} side="top" sideOffset={8}>
                            <AutomationEnabledIcon className="mr-2" />
                        </Tooltip>
                    )}
                </>
            ) : null}

            {badgeNumber && badgeNumber > 0 ? (
                <NavigationItemBadge className="ml-auto">{badgeNumber}</NavigationItemBadge>
            ) : null}

            {newBadge && !collapsed ? (
                <NavigationItemBadge className="border font-medium border-solid rounded-sm border-line-brand-weak bg-surface-brand-weak text-content-brand-default dark:text-content-brand-default ml-auto">
                    {newBadge}
                </NavigationItemBadge>
            ) : null}

            {ellipsis && !collapsed ? <EllipsisIcon className="ml-auto" /> : null}
        </button>
    );
};

export default NavigationButton;
